/**
 * Dedicated WebSocket client for apex-voice.
 *
 * Protocol: apex-voice cmd.* request/response with event broadcast.
 * - Commands: {id, type: "cmd.dial", payload: {...}}
 * - Responses: {id, type: "cmd.dial.result", payload: {...}} or {id, type: "cmd.dial.error", error: {...}}
 * - Events: {id, type: "call.ringing", tenant_id, ts, payload: {...}}
 *
 * Only connects on the WS leader tab (call from softphone init after leader election).
 */

import { trace, debugEmit } from './debug-stream';
import { getUserInfo } from '../auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VoiceWsEvent {
  id: string;
  type: string;
  tenant_id?: string;
  ts?: number;
  payload?: Record<string, unknown>;
  error?: { code: string; message: string };
}

export type VoiceEventListener = (payload: Record<string, unknown>) => void;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RECONNECT_DELAY_MIN_MS = 1000;
const RECONNECT_DELAY_MAX_MS = 30000;
// Heartbeat exists because the browser WebSocket API can't initiate
// protocol-level Ping frames — only auto-respond to server-initiated
// pings. If the TCP layer hasn't noticed a vanished server (apex-voice
// pod crash, proxy idle drop), the browser sits in readyState=OPEN
// while no messages flow and cmd.* sends silently buffer. A round-tripped
// `cmd.ping` lets us detect that and force a reconnect.
const HEARTBEAT_INTERVAL_MS = 25000;
const HEARTBEAT_TIMEOUT_MS = 5000;
// Server sends close-code 1001 (CloseGoingAway) on graceful SIGTERM —
// new pod is already Ready behind the Service so we can reconnect
// immediately instead of waiting out a backoff.
const WS_CLOSE_GOING_AWAY = 1001;
// A token-fetch failure (typically `/auth/voice-token` returning 401 during
// a Keycloak refresh-token race after an apex-voice deploy) used to retry
// silently forever. The rep would sit on a dead WS with no UI signal until
// they reloaded. After this many consecutive token failures we stop the
// backoff loop, mark the connection stale, and dispatch `_stale` so the
// UI can show a Reconnect affordance. Manual `reconnect()` clears the flag.
const MAX_TOKEN_FAILURES_BEFORE_STALE = 3;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let ws: WebSocket | null = null;
let voiceUrl = '';
let getToken: (() => Promise<string>) | null = null;
const pending = new Map<
  string,
  { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: number }
>();
let reqCounter = 0;
let lastEventId = '';
const seenEvents = new Set<string>();
const handlers = new Map<string, VoiceEventListener[]>();
let reconnectDelay = RECONNECT_DELAY_MIN_MS;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let intentionalClose = false;
let tokenFailureCount = 0;
let staleState = false;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Configure the voice WS client. Call once before connect(). */
export function configure(url: string, tokenFn: () => Promise<string>): void {
  voiceUrl = url.replace(/\/$/, '');
  getToken = tokenFn;
}

/** Open the WebSocket connection to apex-voice. Resolves when the socket is open. */
export async function connect(): Promise<void> {
  if (ws && ws.readyState === WebSocket.OPEN) return;
  // If already connecting, wait for that attempt to settle
  if (ws && ws.readyState === WebSocket.CONNECTING) {
    return new Promise<void>((resolve, reject) => {
      const orig = ws!;
      const onOpen = () => {
        orig.removeEventListener('close', onClose);
        resolve();
      };
      const onClose = () => {
        orig.removeEventListener('open', onOpen);
        reject(new Error('VoiceWS connection failed'));
      };
      orig.addEventListener('open', onOpen, { once: true });
      orig.addEventListener('close', onClose, { once: true });
    });
  }
  if (!getToken) throw new Error('voice-ws: call configure() before connect()');

  const t = trace('voice', 'connect');
  intentionalClose = false;
  let token: string;
  try {
    token = await getToken();
  } catch (err) {
    tokenFailureCount++;
    debugEmit(
      'voice',
      'token fetch failed',
      { count: tokenFailureCount, max: MAX_TOKEN_FAILURES_BEFORE_STALE, error: String(err) },
      'warn',
    );
    if (tokenFailureCount >= MAX_TOKEN_FAILURES_BEFORE_STALE) {
      enterStaleState('token_fetch_failed');
    }
    t.fail(err);
    throw err;
  }
  let wsUrl = voiceUrl.replace(/^http/, 'ws') + '/ws?token=' + encodeURIComponent(token);
  const activeOrg = getUserInfo()?.activeOrgId;
  if (activeOrg) wsUrl += '&org_id=' + encodeURIComponent(activeOrg);
  if (lastEventId) wsUrl += '&last_event_id=' + encodeURIComponent(lastEventId);

  return new Promise<void>((resolve, reject) => {
    const socket = new WebSocket(wsUrl);
    ws = socket;
    let settled = false;

    socket.onopen = () => {
      reconnectDelay = RECONNECT_DELAY_MIN_MS;
      tokenFailureCount = 0;
      if (!settled) {
        settled = true;
        t.ok();
        resolve();
      }
      startHeartbeat();
      dispatch('_connected', {});
    };

    socket.onmessage = handleMessage;

    socket.onclose = (evt) => {
      stopHeartbeat();

      // Reject all pending requests
      for (const [, p] of pending) {
        clearTimeout(p.timer);
        p.reject(new Error('WebSocket disconnected'));
      }
      pending.clear();

      // Reject connect() if socket closed before opening
      if (!settled) {
        settled = true;
        t.fail(new Error('VoiceWS connection failed'));
        reject(new Error('VoiceWS connection failed'));
      }

      dispatch('_disconnected', { code: evt.code, intentional: intentionalClose });

      if (intentionalClose) return;
      // Stale state takes over reconnect responsibility — operator action
      // (reconnect()) must clear it before any further attempts. Avoids the
      // silent forever-retry loop on Keycloak token failure.
      if (staleState) return;

      // Server-initiated graceful shutdown (rolling deploy) sends close
      // code 1001 — the new pod is already Ready behind the Service so
      // we can reconnect at 0ms. Other close paths (1006 abnormal,
      // network blip, server crash) use exponential backoff so we don't
      // spam connection attempts while a pod is still starting.
      if (evt.code === WS_CLOSE_GOING_AWAY) {
        reconnectDelay = RECONNECT_DELAY_MIN_MS;
        debugEmit('voice', 'graceful close — reconnecting immediately', {}, 'info');
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => {
          connect().catch((err) => {
            debugEmit(
              'voice',
              'reconnect failed, retrying',
              { error: String(err), delay: reconnectDelay },
              'warn',
            );
            reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_DELAY_MAX_MS);
            scheduleReconnect();
          });
        }, 0);
        return;
      }

      debugEmit('voice', 'disconnected, reconnecting', { delay: reconnectDelay }, 'warn');
      scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose fires after onerror — let onclose handle reject + reconnect
    };
  });
}

/** Handle incoming WebSocket messages. */
function handleMessage(evt: MessageEvent): void {
  let msg: VoiceWsEvent;
  try {
    msg = JSON.parse(evt.data as string) as VoiceWsEvent;
  } catch {
    return;
  }

  // Command response — match by ID
  if (msg.id && pending.has(msg.id)) {
    const p = pending.get(msg.id)!;
    clearTimeout(p.timer);
    pending.delete(msg.id);
    if (msg.error) {
      const err = new Error(msg.error.message) as Error & { code: string };
      err.code = msg.error.code;
      p.reject(err);
    } else {
      p.resolve(msg.payload ?? {});
    }
    return;
  }

  // Server event — deduplicate and dispatch
  if (msg.id && msg.ts !== undefined) {
    if (seenEvents.has(msg.id)) return;
    seenEvents.add(msg.id);
    if (seenEvents.size > 500) {
      const first = seenEvents.values().next().value as string;
      seenEvents.delete(first);
    }
    lastEventId = msg.id;
    dispatch(msg.type, msg.payload ?? {});
  }
}

/** Disconnect cleanly. */
export function disconnect(): void {
  intentionalClose = true;
  stopHeartbeat();
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.close(1000);
    ws = null;
  }
}

/** Send a command and await the response. */
export function request<T = Record<string, unknown>>(
  type: string,
  payload: Record<string, unknown> = {},
  timeout = 10000,
): Promise<T> {
  const t = trace('voice', type, payload);
  return new Promise<T>((resolve, reject) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      const err = new Error('VoiceWS not connected');
      t.fail(err);
      reject(err);
      return;
    }

    const id = 'req_' + ++reqCounter;
    const timer = window.setTimeout(() => {
      pending.delete(id);
      const err = new Error('Request timed out') as Error & { code: string };
      err.code = 'timeout';
      t.fail(err);
      reject(err);
    }, timeout);

    pending.set(id, {
      resolve: (v: unknown) => {
        t.ok();
        resolve(v as T);
      },
      reject: (e: Error) => {
        t.fail(e);
        reject(e);
      },
      timer,
    });
    ws.send(JSON.stringify({ id, type, payload }));
  });
}

/** Register an event handler. Returns unsubscribe function. */
export function on(eventType: string, callback: VoiceEventListener): () => void {
  const list = handlers.get(eventType) ?? [];
  list.push(callback);
  handlers.set(eventType, list);
  return () => {
    const arr = handlers.get(eventType);
    if (arr) {
      const idx = arr.indexOf(callback);
      if (idx >= 0) arr.splice(idx, 1);
    }
  };
}

/** Remove all listeners for an event type, or all listeners if no type given. */
export function off(eventType?: string): void {
  if (eventType) {
    handlers.delete(eventType);
  } else {
    handlers.clear();
  }
}

/** Check if connected. */
export function isConnected(): boolean {
  return ws !== null && ws.readyState === WebSocket.OPEN;
}

/** True when reconnect has been abandoned and a manual `reconnect()` is required. */
export function isStale(): boolean {
  return staleState;
}

export interface VoiceWsStatus {
  connected: boolean;
  stale: boolean;
}

/** Snapshot of current connection state, for UI indicators. */
export function getStatus(): VoiceWsStatus {
  return { connected: isConnected(), stale: staleState };
}

/**
 * Operator-initiated reconnect. Clears stale state, resets the token-failure
 * counter, drops any existing socket, and attempts a fresh `connect()`. The
 * caller-supplied gesture (a button click in the UI) is what permits the
 * downstream Twilio Device's AudioContext to start, so this should always be
 * invoked from a user event handler.
 */
export async function reconnect(): Promise<void> {
  debugEmit('voice', 'manual reconnect requested', { wasStale: staleState }, 'info');
  staleState = false;
  tokenFailureCount = 0;
  intentionalClose = false;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectDelay = RECONNECT_DELAY_MIN_MS;
  if (ws) {
    try {
      ws.close(1000);
    } catch {
      // best-effort
    }
    ws = null;
  }
  await connect();
}

// ---------------------------------------------------------------------------
// Reconnect
// ---------------------------------------------------------------------------

function scheduleReconnect(): void {
  if (staleState) return;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => {
    connect().catch((err) => {
      // Token-fetch failures past the threshold flipped us to stale inside
      // connect(); the next scheduleReconnect short-circuits above. For all
      // other errors keep backing off.
      if (staleState) return;
      debugEmit(
        'voice',
        'reconnect failed, retrying',
        { error: String(err), delay: reconnectDelay },
        'warn',
      );
      reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_DELAY_MAX_MS);
      scheduleReconnect();
    });
  }, reconnectDelay);
}

function enterStaleState(reason: string): void {
  if (staleState) return;
  staleState = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  debugEmit('voice', 'entered stale state', { reason }, 'error');
  dispatch('_stale', { reason });
}

// ---------------------------------------------------------------------------
// Heartbeat
// ---------------------------------------------------------------------------

function startHeartbeat(): void {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    request('cmd.ping', {}, HEARTBEAT_TIMEOUT_MS).catch(() => {
      // No response within HEARTBEAT_TIMEOUT_MS — connection is half-open.
      // Force-close so onclose runs scheduleReconnect. ws may already be
      // null if the close happened concurrently; guard the call.
      debugEmit('voice', 'heartbeat timeout — force-closing', {}, 'warn');
      try {
        ws?.close();
      } catch {
        // Ignore — close is best-effort.
      }
    });
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

function dispatch(type: string, payload: Record<string, unknown>): void {
  if (type !== '_connected') debugEmit('voice', type, payload);
  const list = handlers.get(type);
  if (list) for (const h of list) h(payload);

  // Wildcard listeners
  const wildcard = handlers.get('*');
  if (wildcard) for (const h of wildcard) h({ ...payload, _type: type });

  // DOM event for loose coupling
  window.dispatchEvent(new CustomEvent(`apex:voice:${type}`, { detail: payload }));
}
